import { useState } from "react";
import { View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  Field,
  Row,
  TextField,
} from "~/components/ui";
import type { useAccountPrivacy } from "~/hooks/useAccountPrivacy";
import { isDeletionConfirmed } from "~/utils/account-deletion";
import { spacing } from "~/styles/theme";

/**
 * Account deletion for the Settings screen. The person types DELETE and their
 * email address, which schedules the account for removal after 30 days (the
 * request_account_deletion() database function checks both again). Until then
 * they stay signed in and can cancel.
 */
export function DeleteAccountPanel({
  privacy,
  email,
  summary,
  scheduledLabel,
}: {
  privacy: ReturnType<typeof useAccountPrivacy>;
  email: string;
  /** What gets deleted, in the app's own words. */
  summary: string;
  /** The scheduled deletion date, already formatted for the user. */
  scheduledLabel: string | null;
}) {
  const [confirming, setConfirming] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [typedEmail, setTypedEmail] = useState("");
  const [message, setMessage] = useState("");

  const confirmed = isDeletionConfirmed({
    phrase,
    email: typedEmail,
    accountEmail: email,
  });

  const reset = () => {
    setConfirming(false);
    setPhrase("");
    setTypedEmail("");
  };

  const requestDeletion = async () => {
    setMessage("");
    try {
      await privacy.requestDeletion(typedEmail);
      reset();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Could not schedule deletion.",
      );
    }
  };

  const cancelDeletion = async () => {
    setMessage("");
    try {
      await privacy.cancelDeletion();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Could not cancel deletion.",
      );
    }
  };

  return (
    <Card danger gap="sm">
      <AppText weight="semibold" tone="danger">
        Delete account
      </AppText>
      <AppText size="xs">{summary}</AppText>

      {privacy.isScheduledForDeletion ? (
        <>
          <AppText size="xs">
            {scheduledLabel
              ? `Your account is scheduled for deletion on ${scheduledLabel}. Until then you can cancel and keep everything.`
              : "Your account is scheduled for deletion."}
          </AppText>
          <Row>
            <Button
              variant="outline"
              onPress={() => void cancelDeletion()}
              disabled={privacy.cancelDeletionPending}
            >
              <ButtonText>
                {privacy.cancelDeletionPending
                  ? "Cancelling..."
                  : "Cancel deletion"}
              </ButtonText>
            </Button>
          </Row>
        </>
      ) : confirming ? (
        <View style={{ gap: spacing.s }}>
          <Field label="Type DELETE to confirm">
            <TextField
              accessibilityLabel="Type DELETE to confirm"
              value={phrase}
              onChangeText={setPhrase}
              autoCapitalize="characters"
              autoCorrect={false}
            />
          </Field>
          <Field label="Type your email address">
            <TextField
              accessibilityLabel="Type your email address"
              value={typedEmail}
              onChangeText={setTypedEmail}
              placeholder={email}
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect={false}
              keyboardType="email-address"
            />
          </Field>
          <Row>
            <Button
              variant="danger"
              onPress={() => void requestDeletion()}
              disabled={!confirmed || privacy.requestDeletionPending}
            >
              <ButtonText tone="danger">
                {privacy.requestDeletionPending
                  ? "Scheduling..."
                  : "Delete my account"}
              </ButtonText>
            </Button>
            <Button
              variant="outline"
              onPress={reset}
              disabled={privacy.requestDeletionPending}
            >
              <ButtonText>Keep my account</ButtonText>
            </Button>
          </Row>
        </View>
      ) : (
        <Row>
          <Button variant="danger" onPress={() => setConfirming(true)}>
            <ButtonText tone="danger">Delete account</ButtonText>
          </Button>
        </Row>
      )}

      {message ? (
        <AppText tone="danger" size="xs">
          {message}
        </AppText>
      ) : null}
    </Card>
  );
}
